/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { pgTable, text, serial, integer, boolean, timestamp, jsonb, uuid } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";
import { organizations } from "./organizations";

// User roles enum
export const USER_ROLES = {
  ADMIN: 'admin',
  INSTRUCTOR: 'instructor',
  CURRICULUM_MANAGER: 'curriculum_manager',
  USER: 'user',
  LEARNER: 'learner',
} as const;

export type UserRole = typeof USER_ROLES[keyof typeof USER_ROLES];

// Users table for storing user profiles
export const users = pgTable("users", {
  id: serial("id").primaryKey(),
  uuid: uuid("uuid").defaultRandom().notNull().unique(), // UUID for cross-system identification
  organizationId: uuid("organization_id").notNull().references(() => organizations.id, { onDelete: 'cascade' }), // Every user belongs to an organization (shared or personal)
  name: text("name").notNull(),
  email: text("email").notNull().unique(),
  password: text("password"),  // Nullable for social login users
  photoUrl: text("photo_url"),
  bio: text("bio"),
  role: text("role").default("user"),
  emailVerified: boolean("email_verified").default(false),
  isActive: boolean("is_active").default(true),
  lastLoginAt: timestamp("last_login_at"),
  // Social login provider fields
  googleId: text("google_id"),
  microsoftId: text("microsoft_id"),
  firebaseUid: text("firebase_uid"),
  workosUserId: text("workos_user_id"),
  authProvider: text("auth_provider").default("local"), // 'local', 'google', 'microsoft', 'firebase', 'workos'
  // Remote auth mode fields
  masterUserId: text("master_user_id"), // User ID from master authentication server
  lastSyncedAt: timestamp("last_synced_at"), // Last time user data was synced from master
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Refresh tokens table for JWT authentication
export const refreshTokens = pgTable("refresh_tokens", {
  id: serial("id").primaryKey(),
  token: text("token").notNull().unique(),
  userId: integer("user_id").notNull(),
  expiresAt: timestamp("expires_at").notNull(),
  isRevoked: boolean("is_revoked").default(false),
  createdAt: timestamp("created_at").defaultNow(),
});

// User Progress
export const userProgress = pgTable("user_progress", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull(),
  activityId: integer("activity_id").notNull(),
  progress: integer("progress").default(0),
  isCompleted: boolean("is_completed").default(false),
  lastAccessed: timestamp("last_accessed").defaultNow(),
  completedAt: timestamp("completed_at"),
});

// User Learning Plans
export const userLearningPlans = pgTable("user_learning_plans", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull(),
  planId: integer("plan_id").notNull(),
  progress: integer("progress").default(0),
  completedActivities: integer("completed_activities").default(0),
  totalActivities: integer("total_activities").default(0),
  startDate: timestamp("start_date").defaultNow(),
  lastAccessDate: timestamp("last_access_date").defaultNow(),
  completedDate: timestamp("completed_date"),
  isActive: boolean("is_active").default(true),
});

// User Learning Modules
export const userLearningModules = pgTable("user_learning_modules", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull(),
  moduleId: integer("module_id").notNull(),
  progress: integer("progress").default(0),
  completedPlans: integer("completed_plans").default(0),
  totalPlans: integer("total_plans").default(0),
  startDate: timestamp("start_date").defaultNow(),
  lastAccessDate: timestamp("last_access_date").defaultNow(),
  completedDate: timestamp("completed_date"),
  isActive: boolean("is_active").default(true),
});

// User Learning Paths
export const userLearningPaths = pgTable("user_learning_paths", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull(),
  pathId: integer("path_id").notNull(),
  progress: integer("progress").default(0),
  completedNodes: integer("completed_nodes").default(0),
  totalNodes: integer("total_nodes").default(0),
  currentNodeId: text("current_node_id"), // ID of the current node in the path
  startDate: timestamp("start_date").defaultNow(),
  lastAccessDate: timestamp("last_access_date").defaultNow(),
  completedDate: timestamp("completed_date"),
  isActive: boolean("is_active").default(true),
});

// User Sessions - tracks current chat session for each user
export const userSessions = pgTable("user_sessions", {
  userId: integer("user_id").primaryKey(), // One session per user
  currentChatSessionId: integer("current_chat_session_id"),
  lastActivityAt: timestamp("last_activity_at").defaultNow(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// User Settings - stores user-specific UI and application preferences
export const userSettings = pgTable("user_settings", {
  userId: integer("user_id").primaryKey(), // One settings set per user
  chatSettings: jsonb("chat_settings"), // {deleteConfirmation: boolean, defaultAgent: string, currentSessionId: number, etc}
  uiSettings: jsonb("ui_settings"),     // {theme: 'dark'|'light', sidebarWidth: number, language: string}
  agentSettings: jsonb("agent_settings"), // {defaultAgent: string, agentPreferences: {}}
  learningSettings: jsonb("learning_settings"), // {difficulty: string, autoAdvance: boolean, preferredFormats: []}
  notificationSettings: jsonb("notification_settings"), // {email: boolean, push: boolean, frequency: string}
  integrationSettings: jsonb("integration_settings"), // {googleDriveFolderId: string, googleDriveFolderName: string, etc}
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Insert schemas
export const insertUserSchema = createInsertSchema(users).omit({ 
  id: true,
  uuid: true, // Auto-generated UUID
  createdAt: true, 
  updatedAt: true, 
  lastLoginAt: true 
});

export const insertRefreshTokenSchema = createInsertSchema(refreshTokens).omit({ 
  id: true, 
  createdAt: true 
});

export const insertUserProgressSchema = createInsertSchema(userProgress).omit({ id: true, lastAccessed: true, completedAt: true });
export const insertUserLearningPlanSchema = createInsertSchema(userLearningPlans).omit({ id: true, startDate: true, lastAccessDate: true, completedDate: true });
export const insertUserLearningModuleSchema = createInsertSchema(userLearningModules).omit({ id: true, startDate: true, lastAccessDate: true, completedDate: true });
export const insertUserLearningPathSchema = createInsertSchema(userLearningPaths).omit({ id: true, startDate: true, lastAccessDate: true, completedDate: true });
export const insertUserSessionSchema = createInsertSchema(userSessions).omit({ createdAt: true, updatedAt: true });
export const insertUserSettingsSchema = createInsertSchema(userSettings).omit({ createdAt: true, updatedAt: true });

// Auth-specific schemas
export const registerUserSchema = insertUserSchema.omit({ 
  emailVerified: true, 
  isActive: true,
  password: true,  // We'll redefine this to be optional
  organizationId: true  // We'll redefine this to be optional (auto-created)
}).extend({
  password: z.string().min(8, 'Password must be at least 8 characters').optional(),
  confirmPassword: z.string().optional(),
  organizationId: z.string().uuid().optional(),  // Optional - auto-created if not provided
  authProvider: z.enum(['local', 'google', 'microsoft', 'workos']).default('local')
}).refine((data) => {
  // For local auth, password is required and must match
  if (data.authProvider === 'local') {
    if (!data.password) return false;
    if (data.password !== data.confirmPassword) return false;
  }
  return true;
}, {
  message: "For local registration, password is required and passwords must match",
  path: ["password"],
});

export const loginUserSchema = z.object({
  email: z.string().email('Invalid email format'),
  password: z.string().min(1, 'Password is required')
});

// Social login user creation schema
export const socialUserSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  email: z.string().email('Valid email is required'),
  photoUrl: z.string().url().optional(),
  googleId: z.string().optional(),
  microsoftId: z.string().optional(),
  workosUserId: z.string().optional(),
  authProvider: z.enum(['google', 'microsoft', 'workos']),
  role: z.string().default('user')
});

// Types
export type User = typeof users.$inferSelect;
export type InsertUser = z.infer<typeof insertUserSchema>;
export type RegisterUser = z.infer<typeof registerUserSchema>;
export type LoginUser = z.infer<typeof loginUserSchema>;
export type RefreshToken = typeof refreshTokens.$inferSelect;
export type InsertRefreshToken = z.infer<typeof insertRefreshTokenSchema>;
export type UserProgress = typeof userProgress.$inferSelect;
export type InsertUserProgress = z.infer<typeof insertUserProgressSchema>;
export type UserLearningPlan = typeof userLearningPlans.$inferSelect;
export type InsertUserLearningPlan = z.infer<typeof insertUserLearningPlanSchema>;
export type UserLearningModule = typeof userLearningModules.$inferSelect;
export type InsertUserLearningModule = z.infer<typeof insertUserLearningModuleSchema>;
export type UserLearningPath = typeof userLearningPaths.$inferSelect;
export type InsertUserLearningPath = z.infer<typeof insertUserLearningPathSchema>;
export type UserSession = typeof userSessions.$inferSelect;
export type InsertUserSession = z.infer<typeof insertUserSessionSchema>;
export type UserSettings = typeof userSettings.$inferSelect;
export type InsertUserSettings = z.infer<typeof insertUserSettingsSchema>; 